import React, { useState, useEffect } from 'react';
import { Modal, CircularProgress, Alert, Box, FormControl, InputAdornment, Snackbar, Typography, TextField, Tooltip, Button, Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import { grey, red } from '@mui/material/colors';
import AddIcon from '@mui/icons-material/Add';
import BASE_URL from '../../config'

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'white',
  borderRadius: 4, 
  px: 4,
  py: 3,
  outline: 'none',
  width: { xs: '90%', sm: '500px' },
  maxHeight: 'auto',
  overflowY: 'auto',
  '&::-webkit-scrollbar': { display: 'none' },
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
};

export default function Modalnewrol({ refresh }) {
  const [clientData, setClientData] = useState({ nombre: '', empresa: '', email: '', phone: '', direccion: '', equipo: [] });
  const [confirmType, setConfirmType] = useState(null);
  const [open, setOpen] = useState(false);
  const [nombresBD, setNombresBD] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const isUsernameValid = clientData.nombre.trim().length > 6;
  const isCompanyValid = clientData.empresa.trim().length > 1;
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientData.email);
  const isPhoneValid = clientData.phone.length === 10;
  const isAdresseValid = clientData.direccion.trim().length > 10;

  useEffect(() => {
    const fetchEquipos = async () => {
      try {
        const res = await fetch(`${BASE_URL}/clientes/getequipos`);
        const data = await res.json();
        setNombresBD(data);
      } catch (err) {
        console.error("Error cargando equipos:", err);
      }
    };
    if (open) fetchEquipos();
  }, [open]);

  useEffect(() => {
    if (open) {
      setClientData({ nombre: '', empresa: '', email: '', phone: '', direccion: '', equipo: [] });
      setConfirmType(null);
      setError('');
    }
  }, [open]);

  const handleCheck = (nombreEquipo) => {
    const { equipo } = clientData;
    const currentIndex = equipo.indexOf(nombreEquipo);
    const newEquipo = [...equipo];
    if (currentIndex === -1) { newEquipo.push(nombreEquipo); }
    else { newEquipo.splice(currentIndex, 1); }
    setClientData({ ...clientData, equipo: newEquipo });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const limits = { empresa: 35, nombre: 40, email: 35, direccion: 60, phone: 10 };
    if (limits[name] && value.length > limits[name]) return;
    if (name === 'phone') {
      const onlyNums = value.replace(/[^0-9]/g, '');
      if (onlyNums.length <= 10) setClientData({ ...clientData, [name]: onlyNums });
      return;
    }
    setClientData({ ...clientData, [name]: value });
  };

  const validateAndOpenSave = () => {
    if (!clientData.nombre.trim() || !clientData.empresa.trim() || !clientData.email.trim() || !clientData.phone.trim() || !clientData.direccion.trim() || clientData.equipo.length === 0) {
      setError(true);
    } else {
      setError(false);
      setConfirmType('save');
    }
  };

  const resetModal = () => {
    setClientData({ nombre: '', empresa: '', email: '', phone: '', direccion: '', equipo: [] });
    setConfirmType(null);
    setError(false);
    handleClose();
  };

  const handleClose = () => {
    setConfirmType(null);
    setLoading(false);
    setError('');
    setClientData({ nombre: '', empresa: '', email: '', phone: '', direccion: '', equipo: [] });
    setOpen(false);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BASE_URL}/clientes/nuevocliente`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(clientData)
      });
      const data = await res.json();
      if (res.ok) {
        setSnackbar({ open: true, message: 'Cliente creado con éxito', severity: 'success' });
        setTimeout(() => { if (refresh) refresh(); handleClose(); }, 1500);
      } else {
        setError(data.message || 'Error al registrar el cliente.');
        setConfirmType(null);
        setLoading(false);
      }
    } catch (e) {
      setError('Error de conexión con el servidor.');
      setConfirmType(null);
      setLoading(false);
    }
  };

  const boxstyle = {
    mx: 0.3, my: 0.3, maxHeight: '60px', overflowY: 'auto', bgcolor: '#ffffff',
    '&::-webkit-scrollbar': { width: '8px' },
    '&::-webkit-scrollbar-thumb': { background: grey[300], borderRadius: '8px' },
    '&::-webkit-scrollbar-thumb:hover': { background: grey[500] },
  };

  const Inputstyle = {
    '& .MuiOutlinedInput-root': {
      bgcolor: '#f5f5f5', borderRadius: '8px',
      '& fieldset': { borderColor: 'transparent' },
      '&:hover fieldset': { borderColor: grey[400] },
      '&.Mui-focused fieldset': { borderColor: grey[600] },
    },
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} fullWidth sx={{ borderRadius: '8px', bgcolor: 'black', textTransform: 'none' }} variant="contained" startIcon={<AddIcon />}>
        Nuevo cliente
      </Button>

      <Modal 
        open={open} 
        onClose={() => setConfirmType('cancel')}
        disableRestoreFocus
        disableEnforceFocus
      >
        <Box sx={style}>
          {confirmType ? (
            /* VISTA DE CONFIRMACIÓN */
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="h6" textAlign="start">
                {confirmType === 'cancel' ? '¿Cancelar nuevo cliente?' : '¿Crear nuevo cliente?'}
              </Typography>
              <Typography variant="body1" color='textSecondary'>
                {confirmType === 'cancel' ? 'Los datos ingresados se perderán. ¿Estás seguro que deseas cancelar?' : '¿Estás seguro que deseas crear este nuevo cliente?'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 1, justifyContent: 'end' }}>
                <Button fullWidth variant="contained" sx={{ bgcolor: grey[100], color: 'black', textTransform: 'none', width: { xs: '50%', sm: '42%' } }} onClick={() => setConfirmType(null)}>
                  {confirmType === 'cancel' ? 'No, continuar editando' : 'No, revisar datos'}
                </Button>
                <Button fullWidth variant="contained" sx={{ bgcolor: 'black', textTransform: 'none', width: { xs: '50%', sm: '25%' } }} onClick={confirmType === 'cancel' ? resetModal : handleSubmit}>
                  {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : confirmType === 'cancel' ? 'Sí, cancelar' : 'Sí, guardar'}
                </Button>
              </Box>
            </Box>
          ) : (
            /* VISTA DE FORMULARIO */
            <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
              <Box>
                <Typography variant="h6">Registrar Nuevo Cliente</Typography>
                <Typography variant="subtitle1" color='textSecondary'>Completa la información del cliente</Typography>
              </Box>

              <FormControl>
                <Typography sx={{ mb: 0.5 }}>Empresa</Typography>
                <TextField size='small' fullWidth name='empresa' sx={Inputstyle} error={(clientData.empresa.length > 0 && !isCompanyValid) || (error && clientData.empresa.length === 0)} helperText={(clientData.empresa.length > 0 && !isCompanyValid) || (error && clientData.empresa.length === 0) ? "Ingresa el nombre de la empresa" : ""} value={clientData.empresa} onChange={handleInputChange} />
              </FormControl>

              <FormControl>
                <Typography sx={{ mb: 0.5 }}>Nombre del Contacto</Typography>
                <TextField size='small' fullWidth sx={Inputstyle} value={clientData.nombre} name='nombre' error={(clientData.nombre.length > 0 && !isUsernameValid) || (error && clientData.nombre.length === 0)} helperText={(clientData.nombre.length > 0 && !isUsernameValid) || (error && clientData.nombre.length === 0) ? "Ingresa el nombre del contacto de la empresa" : ""} onChange={handleInputChange} />
              </FormControl>

              <FormControl>
                <Typography sx={{ mb: 0.5 }}>Email</Typography>
                <TextField size='small' fullWidth name='email' sx={Inputstyle} error={(clientData.email.length > 0 && !isEmailValid) || (error && clientData.email.length === 0)} helperText={(clientData.email.length > 0 && !isEmailValid) || (error && clientData.email.length === 0) ? "Ingresa un correo válido" : ""} value={clientData.email} onChange={handleInputChange} />
              </FormControl>

              <FormControl>
                <Typography sx={{ mb: 0.5 }}>Teléfono</Typography>
                <TextField size='small' name='phone' fullWidth sx={Inputstyle} error={(clientData.phone.length > 0 && !isPhoneValid) || (error && clientData.phone.length === 0)} helperText={(clientData.phone.length > 0 && !isPhoneValid) || (error && clientData.phone.length === 0) ? "Se requieren 10 dígitos" : ""} value={clientData.phone} onChange={handleInputChange} InputProps={{ startAdornment: <InputAdornment position="start">+52</InputAdornment> }} />
              </FormControl>

              <FormControl>
                <Typography sx={{ mb: 0.5 }}>Dirección</Typography>
                <TextField variant='outlined' fullWidth name='direccion' multiline rows={2} sx={Inputstyle} error={(clientData.direccion.length > 0 && !isAdresseValid) || (error && clientData.direccion.length === 0)} helperText={(clientData.direccion.length > 0 && !isAdresseValid) || (error && clientData.direccion.length === 0) ? "Complete el campo" : ""} value={clientData.direccion} onChange={(e) => setClientData({ ...clientData, direccion: e.target.value })} />
              </FormControl>

              <FormGroup>
                <Typography sx={{ mb: 0.5 }}>Equipos</Typography>
                <Tooltip title="Debes seleccionar al menos un equipo" open={error && clientData.equipo.length === 0} placement="top-end" disableInteractive arrow componentsProps={{ tooltip: { sx: { bgcolor: red[700], fontWeight: 'bold' } }, arrow: { sx: { color: red[700] } } }}>
                  <div>
                    <Box sx={{ border: `1px solid ${error && clientData.equipo.length === 0 ? red[700] : grey[300]}`, borderRadius: '8px' }}>
                      <Box sx={boxstyle}>
                        {nombresBD.length > 0 ? nombresBD.map((nombre) => (
                          <Box 
                            key={nombre} 
                            sx={{ 
                              ':hover': { bgcolor: grey[100] }, 
                              display: 'flex', 
                              alignItems: 'center', 
                              height: '30px', 
                              transition: 'background-color 0.2s ease' }}>
                            <FormControlLabel control={<Checkbox sx={{ ml: 1, color: grey[400], '&.Mui-checked': { color: 'black' } }} size="small" checked={clientData.equipo.includes(nombre)} onChange={() => handleCheck(nombre)} />} label={<Typography variant="body2" sx={{ userSelect: 'none', fontWeight: 500 }}>{nombre}</Typography>} sx={{ margin: 0, width: '100%' }} />
                          </Box>
                        )) : (
                          <Box sx={{textAlign: 'center' }}>
                            <Typography variant="caption" sx={{ color: grey[500], fontStyle: 'italic' }}>No hay equipos registrados.</Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </div>
                </Tooltip>
              </FormGroup>

              <Box sx={{mt: 1, display: 'flex', flexDirection: 'row', justifyContent: 'end' }}>
                <Button variant="contained" sx={{ bgcolor: grey[100], color: 'black', width: { xs: '50%', sm: '25%' }, textTransform: 'none', mr: 1, '&:hover': { bgcolor: grey[200] } }} onClick={() => setConfirmType('cancel')}>
                  Cancelar
                </Button>
                <Button variant="contained" sx={{ bgcolor: 'black', ml: 1, textTransform: 'none', width: { xs: '50%', sm: '30%' } }} onClick={validateAndOpenSave}>
                  {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : "Crear cliente"}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Modal>

      <Snackbar open={snackbar.open} autoHideDuration={2500} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%', borderRadius: '10px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}